import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom'; // ✅ added useLocation
import { useAssessmentService, AssessmentType } from '../services/assessmentService';
import MeetingGrid from '../components/MeetingGrid';
import GradingView from '../components/GradingView';
import AssessmentModal from '../components/AssessmentModal';
import PreAssessmentModal from '../components/MeetingPreAssessmentModal';
import NavigationConfirmDialog from '../components/MeetingNavigationConfirmDialog';
import { useNavigationBlock } from '../contexts/NavigationBlockContext';

const MeetingPage: React.FC = () => {
  const { assessmentId } = useParams();
  const { fetchAssessment } = useAssessmentService();
  const [assessment, setAssessment] = useState<AssessmentType | null>(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const fromQuickAssign = location.state?.fromQuickAssign === true;
  const assignmentIdFromState = location.state?.assignmentId as number | undefined;
  const hasValidAssignmentId = Number.isFinite(assignmentIdFromState) && Number(assignmentIdFromState) > 0;

  // AssessmentModal only opens when redirected from QuickAssign
  const [assessmentModalOpen, setAssessmentModalOpen] = useState(fromQuickAssign);
  // PreAssessmentModal opens immediately if NOT from QuickAssign (original behavior)
  const [preAssessmentOpen, setPreAssessmentOpen] = useState(!fromQuickAssign);

  // For switching between meeting and grading view
  const [gradingActive, setGradingActive] = useState(false);
  const [gradingProps, setGradingProps] = useState<any>(null);

  // Navigation block context
  const navigate = useNavigate();
  const {
    isBlocked,
    setBlocked,
    navDialogOpen,
    confirmNavigation,
    cancelNavigation,
    registerNavigate
  } = useNavigationBlock();

  useEffect(() => {
    if (registerNavigate) registerNavigate(navigate);
  }, [registerNavigate, navigate]);

  // Fetch assessment
  useEffect(() => {
    if (!assessmentId) {
      setLoading(false);
      return;
    }
    fetchAssessment(assessmentId)
      .then(setAssessment)
      .catch(() => setAssessment(null))
      .finally(() => setLoading(false));
  }, [assessmentId]);

  // Enable navigation blocking only after BOTH modals are closed
  useEffect(() => {
    if (!assessmentModalOpen && !preAssessmentOpen) {
      setBlocked(true);
    }
  }, [assessmentModalOpen, preAssessmentOpen, setBlocked]);

  // Block browser-level navigation (refresh/close/back)
  useEffect(() => {
    if (!isBlocked) return;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = 'Leaving will end your session. Are you sure?';
      return 'Leaving will end your session. Are you sure?';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isBlocked]);

  const handleEndMeeting = (
    transcript: { speaker: string; text: string }[],
    conversationId: string,
    dateCompleted: string
  ) => {
    if (!hasValidAssignmentId) {
      setBlocked(false);
      navigate('/assessment', {
        state: {
          assignmentError: 'Unable to complete grading because assignment id is missing. Please relaunch from your assigned assessments list.',
        },
      });
      return;
    }

    setGradingProps({
      transcript,
      conversationId,
      assessmentId,
      dateCompleted,
      assignmentId: assignmentIdFromState,
    });
    setGradingActive(true);
    setBlocked(false);
  };
  if (loading) return <div>Loading assessment...</div>;
  if (!assessment) return <div>Assessment not found.</div>;
  if (assessmentModalOpen) {
    return (
      <AssessmentModal
        open={assessmentModalOpen}
        onClose={() => navigate('/')}
        title={assessment.title}
        duration={assessment.duration}
        meetingSummary={assessment.meetingSummary}
        goals={assessment.goals}
        companyName={assessment.companyName}
        companyIndustry={assessment.companyIndustry}
        personaName={assessment.personaName}
        personaRole={assessment.personaRole}
        companyLocations={assessment.companyLocations}
        assessmentId={assessment.id}
        completeStatus={false}
        isAvailable={true}
        onStart={() => {
          setAssessmentModalOpen(false);
          setPreAssessmentOpen(true);
        }}
      />
    );
  }
  if (preAssessmentOpen) {
    return (
      <PreAssessmentModal
        open={preAssessmentOpen}
        onVerified={() => setPreAssessmentOpen(false)}
      />
    );
  }
  if (gradingActive && gradingProps) {
    return (
      <GradingView
        transcript={gradingProps.transcript}
        conversationId={gradingProps.conversationId}
        assessmentId={gradingProps.assessmentId}
        assessmentTitle={assessment.title}
        dateCompleted={gradingProps.dateCompleted}
        assignmentId={gradingProps.assignmentId}
      />
    );
  }
  return (
    <>
      <MeetingGrid
        assessment={assessment}
        onEndMeeting={handleEndMeeting}
      />
      <NavigationConfirmDialog
        open={navDialogOpen}
        onConfirm={confirmNavigation}
        onCancel={cancelNavigation}
      />
    </>
  );
};

export default MeetingPage;